import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, Modal, Form, Offcanvas } from 'react-bootstrap';
import ReactPaginate from 'react-paginate';
import { useAuth } from '../../context/AuthContext';
import withPermission from '../../utils/withPermission';
import Swal from 'sweetalert2';
import { FaFilter } from "react-icons/fa";
import Select from 'react-select';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const API_URL = `${backendUrl}/api/users`;

const UserDetails = () => {
    const [users, setUsers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ name: '' });
    const [isEdit, setIsEdit] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const didFetchRef = useRef(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [roles, setRoles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [selectedRole, setSelectedRole] = useState('');
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const WithDeletePermission = withPermission(({ children }) => children, ['user-delete']);
    const WithEditPermission = withPermission(({ children }) => children, ['user-edit']);
    const WithCreatePermission = withPermission(({ children }) => children, ['user-create']);
    const [searchQuery, setSearchQuery] = useState('');
    const debounceTimeoutRef = useRef(null);
    const [errors, setErrors] = useState({});
    const [dateFilterDropdown, setDateFilterDropdown] = useState(false);
    const [dateRange, setDateRange] = useState({ from: '', to: '' });
    useEffect(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            fetchUsers(1, searchQuery);
        }, searchQuery ? 1500 : 0);

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [searchQuery]);
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);

    };

    const fetchUsers = async (page = 1) => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');

            const token = userInfo?.token;
            let params = { page };

            if (searchQuery) params.search = searchQuery;
            if (dateRange.from) params.from_date = dateRange.from;
            if (dateRange.to) params.to_date = dateRange.to;
            const response = await axios.get(`${API_URL}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params,

            });
            const { data, current_page, last_page } = response.data.users;
            const filteredUsers = data.filter(
                (user) => !user.roles?.some((role) => role.name === "superadmin")
            );

            setUsers(filteredUsers);
            // setUsers(data);
            // setPageCount(response.data.last_page);
            // setCurrentPage(response.data.current_page);
            setCurrentPage(current_page - 1);
            setPageCount(last_page);

        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };
    const fetchRoles = async () => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;
            const response = await axios.get(`${backendUrl}/api/roles`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });
            setRoles(response.data.data);
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };
    const fetchDepartments = async () => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;
            const response = await axios.get(`${backendUrl}/api/departments`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });
            if (response.data.status === 'success') {
                setDepartments(response.data.data.map(d => ({ value: d.id, label: d.name })));
            }
        } catch (error) {
            console.error('Error fetching departments:', error);
        }
    };

    useEffect(() => {
        if (showModal) {
            if (roles.length === 0) fetchRoles();
            fetchDepartments();
        }
    }, [showModal]);

    const handleAddUser = () => {
        setFormData({ name: '', email: '', password: '' });
        setSelectedRole('');
        setSelectedDepartments([]);
        setIsEdit(false);
        setSelectedUser(null);
        setShowModal(true);
        setErrors({});
    };

    const handleEditUser = (user) => {
        setFormData({
            name: user.name,
            email: user.email,
            password: '', // Always empty
        });
        setSelectedRole(user.roles[0]?.name || null);
        setSelectedDepartments(user.departments?.map(d => ({ value: d.id, label: d.name })) || []);
        setIsEdit(true);
        setSelectedUser(user);
        setShowModal(true);
        setErrors({});
    };


    const handleDelete = async (user) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const userInfo = JSON.parse(localStorage.getItem('user-info'));
                    const origin = localStorage.getItem('user-origin');
                    const token = userInfo?.token;

                    await axios.delete(`${API_URL}/${user.id}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Tenant-Origin': origin,
                        },
                    });

                    setUsers(users.filter(u => u.id !== user.id));
                    Swal.fire("Deleted!", "The user has been deleted.", "success");
                } catch (error) {
                    console.error('Error deleting user:', error);
                    Swal.fire("Error!", "Failed to delete user. Please try again.", "error");
                }
            }
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: undefined,
        }));
    };

    const handlePageChange = ({ selected }) => {
        const newPage = selected + 1;
        setCurrentPage(newPage);
        fetchUsers(newPage);
    };

    useEffect(() => {
        if (!showModal) {
            setErrors({});
        }
    }, [showModal]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name) newErrors.name = 'Name is required';
        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'The email field must be a valid email address';
        }
        if (!isEdit) {
            if (!formData.password) {
                newErrors.password = 'Password is required';
            } else if (formData.password.length < 8) {
                newErrors.password = 'Password must be at least 8 characters long';
            }
        } else {
            // If password is provided, validate its length
            if (formData.password && formData.password.length < 8) {
                newErrors.password = 'Password must be at least 8 characters long';
            }
        }


        const duplicateEmail = users.some(
            (user) => user.email === formData.email && user.id !== (selectedUser?.id || null)
        );
        if (duplicateEmail) {
            newErrors.email = 'Email must be unique';
        }

        return newErrors;
    };
    const [loading, setLoading] = useState(false);

    const handleSubmit = async () => {
        const userInfo = JSON.parse(localStorage.getItem('user-info'));
        const origin = localStorage.getItem('user-origin');
        const token = userInfo?.token;

        // ✅ First validate form
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return; // Stop if validation fails
        }

        setLoading(true); // Optional: show loading indicator

        // ✅ After validation, prepare data to send
        const data = {
            ...formData,
            roles: [selectedRole],
            department_ids: selectedDepartments.map(d => d.value)
        };

        // ✅ If editing and password is empty or only spaces, remove it
        if (isEdit && (!formData.password || formData.password.trim() === "")) {
            delete data.password;
        }

        try {
            let response;
            if (isEdit) {
                // ✅ Update user without empty password
                response = await axios.put(`${API_URL}/${selectedUser.id}`, data, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                });
                setUsers(users.map((ind) => (ind.id === selectedUser.id ? response.data.user : ind)));

            } else {
                // ✅ Create new user
                response = await axios.post(API_URL, data, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                });
                setUsers([...users, response.data.user]);
}
            setErrors({});
            setShowModal(false);
        } catch (error) {
            console.error("Unexpected error: ", error);
        } finally {
            setLoading(false);
        }
    };
    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setDateRange((prev) => ({ ...prev, [name]: value }));
    };
    const applyDateFilter = () => {
        setDateFilterDropdown(false); // Close the dropdown after applying
        fetchUsers();
    };
    const clearDateFilter = () => {
        setDateRange({ from: "", to: "" }); // Reset date inputs
    };















   const handleImport = async (event) => {
            const file = event.target.files[0];
            if (!file) return;
    
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const userInfo = JSON.parse(localStorage.getItem('user-info'));
                const origin = localStorage.getItem('user-origin');
                const token = userInfo?.token;
    
                const formData = new FormData();
                formData.append('file', file);
    
                const response = await axios.post(
                    `${backendUrl}/api/users/import`,
                    formData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data',
                            'Tenant-Origin': origin,
                        },
                    }
                );
    
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: response.data.message || ` Imported successfully!`,
                    timer: 3000,
                    timerProgressBar: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        const container = Swal.getContainer();
                        container.style.top = '50px';
                    },
                });
    
                // Refresh the list after import
                setTimeout(() => window.location.reload(), 1000);
    
                // Clear the file input
                event.target.value = '';
    
            } catch (error) {
                console.error('Import error:', error);
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: error.response?.data?.errors || error.response?.errors || `Failed to import instance information`,
                    timer: 3000,
                    timerProgressBar: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        const container = Swal.getContainer();
                        container.style.top = '50px';
                    },
                });
                event.target.value = "";
    
            } finally {
            }
        };
    
        // Export Functions
        const handleExport = async () => {
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const userInfo = JSON.parse(localStorage.getItem('user-info'));
                const origin = localStorage.getItem('user-origin');
                const token = userInfo?.token;
    
                const response = await axios.get(
                    `${backendUrl}/api/users/export`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Tenant-Origin': origin,
                        },
                        responseType: 'blob', // Important for file download
                    }
                );
    
                // Create blob link and download
                const blob = new Blob([response.data]);
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
    
                // Get filename from response headers or use default
                const contentDisposition = response.headers['content-disposition'];
                let filename = `Users_${new Date().toISOString().split('T')[0]}.xlsx`;
    
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
                    if (filenameMatch.length === 2) {
                        filename = filenameMatch[1];
                    }
                }
    
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
    
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: `Exported successfully!`,
                    timer: 2000,
                    timerProgressBar: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        const container = Swal.getContainer();
                        container.style.top = '50px';
                    },
                });
    
            } catch (error) {
                console.error('Export error:', error);
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: `Failed to export`,
                    timer: 3000,
                    timerProgressBar: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        const container = Swal.getContainer();
                        container.style.top = '50px';
                    },
                });
            } finally {
            }
        };

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid ">
                    <div className="row w-100">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">User</h4>
                                <div className="page-title-right">
                                    <ol className="breadcrumb m-0">
                                        <li className="breadcrumb-item"><a href="javascript:void(0);">User</a></li>
                                        <li className="breadcrumb-item active">User List</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12 ">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between mb-3">
                                        <div className="d-flex">
                                            <div className="search-box me-2 mb-2 d-inline-block">
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Search..."
                                                        value={searchQuery}
                                                        onChange={handleSearchChange}
                                                    />
                                                    <i className="bx bx-search-alt search-icon" />
                                                </div>
                                            </div>



                                        </div>
                                           <div className="d-flex align-items-center gap-2">
                                            {/* Export Button */}
                                            <button
                                                type="button"
                                                className="btn btn-light border"
                                                onClick={handleExport}
                                                data-bs-toggle="tooltip"
                                                data-bs-placement="top"
                                                title="Export"
                                            >
                                                <i className="mdi mdi-file-excel text-success"></i>
                                            </button>

                                            {/* Import Button */}
                                            <input
                                                type="file"
                                                accept=".xlsx, .xls"
                                                id="importLeadsFile"
                                                onChange={handleImport}
                                                style={{ display: "none" }}
                                            />
                                            <label
                                                htmlFor="importLeadsFile"
                                                className="btn btn-light border mb-0"
                                                data-bs-toggle="tooltip"
                                                data-bs-placement="top"
                                                title="Import"
                                            >
                                                <i className="mdi mdi-upload text-primary"></i>
                                            </label>
                                        <WithCreatePermission>
                                            <button
                                                type="button"
                                                className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button"

                                                onClick={handleAddUser} data-bs-toggle="tooltip"
                                                data-bs-placement="top"
                                                title="Add User"
                                            >
                                                <i className="mdi mdi-plus" ></i>
                                            </button>                                            </WithCreatePermission>
                                    </div>
                                    </div>
                                    <div>
                                        <table className="table align-middle table-nowrap table-check table-list">

                                            <thead className="table-sales-gradient">
                                                <tr>
                                                    <th style={{ width: '20px' }} className="align-middle">
                                                        <div className="form-check font-size-16">
                                                            <input className="form-check-input" type="checkbox" id="checkAll" />
                                                            <label className="form-check-label" htmlFor="checkAll"></label>
                                                        </div>
                                                    </th>
                                                    <th className="align-middle">#</th>
                                                    <th className="align-middle"> User Name</th>
                                                    <th className="align-middle">Email</th>
                                                    <th className="align-middle">Role</th>

                                                    <th className="align-middle">Created On
                                                        <div className="dropdown d-inline-block">
                                                            <FaFilter
                                                                className="ms-2 cursor-pointer filter-color"
                                                                onClick={() => setDateFilterDropdown(!dateFilterDropdown)}
                                                            />
                                                            {dateFilterDropdown && (
                                                                <div className="dropdown-menu show p-3" style={{ minWidth: '250px' }}>
                                                                    <label className="form-label">From</label>
                                                                    <input type="date" className="form-control mb-2" name="from" value={dateRange.from} onChange={handleDateChange} />

                                                                    <label className="form-label">To</label>
                                                                    <input type="date" className="form-control mb-2" name="to" value={dateRange.to} onChange={handleDateChange} />

                                                                    <div className="text-end mt-3">
                                                                        <button className="btn btn-primary btn-sm d-inline-block w-auto me-2" onClick={applyDateFilter}>
                                                                            Apply
                                                                        </button>
                                                                        <button className="btn btn-secondary btn-sm d-inline-block w-auto" onClick={clearDateFilter}>
                                                                            Clear
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </th>

                                                    <th className="align-middle">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Array.isArray(users) && users.length > 0 ? (
                                                    users.map((user, index) => (
                                                        <tr key={user.id}>
                                                            <td>
                                                                <div className="form-check font-size-16">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        id={`instanceCheck${index}`}
                                                                    />
                                                                    <label
                                                                        className="form-check-label"
                                                                        htmlFor={`instanceCheck${index}`}
                                                                    ></label>
                                                                </div>
                                                            </td>
                                                            <td>{index + 1}</td>
                                                            <td>{user.name}</td>
                                                            <td>{user.email}</td>
                                                            <td>
                                                                {user.roles && user.roles.length > 0 ? (
                                                                    user.roles.map((role, idx) => (
                                                                        <span key={idx} >
                                                                            {role.name}
                                                                        </span>
                                                                    ))
                                                                ) : (
                                                                    <span className="text-muted"></span>
                                                                )}
                                                            </td>


                                                            <td>{new Date(user.created_at).toLocaleDateString()}</td>

                                                            <td>
                                                                <div className="dropdown">
                                                                    <i
                                                                        className="mdi mdi-dots-horizontal font-size-18"
                                                                        data-bs-toggle="dropdown"
                                                                        aria-expanded="false"
                                                                        style={{ cursor: 'pointer' }}
                                                                    ></i>
                                                                    <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                                                        <li><WithEditPermission>
                                                                            <button className="dropdown-item" onClick={() => handleEditUser(user)}>
                                                                                Edit
                                                                            </button></WithEditPermission>
                                                                        </li>
                                                                        <li><WithDeletePermission>
                                                                            <button className="dropdown-item" onClick={() => handleDelete(user)}>
                                                                                Delete
                                                                            </button></WithDeletePermission>
                                                                        </li>
                                                                    </ul>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="6" className="text-center">No users found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="d-flex justify-content-end pagination-sm">
                                        <ReactPaginate
                                            previousLabel={'<'}
                                            nextLabel={'>'}
                                            breakLabel={'...'}
                                            pageCount={pageCount}
                                            marginPagesDisplayed={2}
                                            pageRangeDisplayed={3}
                                            onPageChange={handlePageChange}
                                            containerClassName={'pagination pagination-rounded justify-content-end mb-2'}
                                            pageClassName={'page-item'}
                                            pageLinkClassName={'page-link'}
                                            previousClassName={'page-item previous'}
                                            previousLinkClassName={'page-link'}
                                            nextClassName={'page-item next'}
                                            nextLinkClassName={'page-link'}
                                            breakClassName={'page-item'}
                                            breakLinkClassName={'page-link'}
                                            activeClassName={'active'}
                                        />
                                    </div>
                                    {/* Modal for Add/Edit User */}
                                    <Offcanvas show={showModal} onHide={() => setShowModal(false)} placement='end' style={{ width: '450px' }}>
                                        <Offcanvas.Header closeButton>
                                            <Offcanvas.Title>{isEdit ? 'Edit User' : 'Add User'}</Offcanvas.Title>
                                        </Offcanvas.Header>
                                        <hr style={{ margin: 0 }} /><br />


                                        <Offcanvas.Body>
                                            <Form>
                                                <Form.Group controlId="userName" className='mb-3'>
                                                    <Form.Label>User Name</Form.Label>
                                                    <Form.Control type="text" name="name" value={formData.name} onChange={handleInputChange}
                                                        className={`form-control ${errors?.name ? 'is-invalid' : ''}`}

                                                    />

                                                    {errors.name && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.name}</div>}


                                                </Form.Group>

                                                <Form.Group controlId="useremail" className='mb-3'>
                                                    <Form.Label>Email</Form.Label>
                                                    <Form.Control type="email" name="email" value={formData.email} onChange={handleInputChange}
                                                        className={`form-control ${errors?.email ? 'is-invalid' : ''}`}

                                                    />
                                                    {errors.email && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.email}</div>}

                                                </Form.Group>


                                                <Form.Group controlId="userpass" className='mb-3'>
                                                    <Form.Label>password</Form.Label>
                                                    <Form.Control type="password" name="password" onChange={handleInputChange} className={`form-control ${errors?.password ? 'is-invalid' : ''}`} />
                                                    {errors.password && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.password}</div>}
                                                </Form.Group>

                                                <Form.Group controlId="userRole" className='mb-3'>
                                                    <Form.Label>Role</Form.Label>
                                                    <Form.Select
                                                        value={selectedRole}
                                                        onChange={(e) => setSelectedRole(e.target.value)}
                                                    >
                                                        <option value="">Select Role</option>
                                                        {roles.map((role) => (
                                                            <option key={role.id} value={role.name}>
                                                                {role.name}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>

                                                <Form.Group controlId="userDepartments" className='mb-3'>
                                                    <Form.Label>Departments</Form.Label>
                                                    <Select
                                                        isMulti
                                                        options={departments}
                                                        value={selectedDepartments}
                                                        onChange={setSelectedDepartments}
                                                        placeholder="Select Departments"
                                                        classNamePrefix="react-select"
                                                    />
                                                </Form.Group>
                                            </Form>
                                        </Offcanvas.Body>

                                        <div className="p-3 d-flex justify-content-end border-top">
                                            <Button variant="secondary me-2" onClick={() => setShowModal(false)}>Close</Button>
                                            <Button variant="secondary" onClick={handleSubmit}>{isEdit ? 'Update' : 'Save'} </Button>
                                        </div>
                                    </Offcanvas>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDetails;
